import { Event, SubEvent } from 'inscreva-sse-models';
import set from 'lodash.set';

import { FormStep } from './step';

export type FieldType = 'field' | 'option';

export type Priority = {
  level?: number;
  position?: number;
  group: string;
};

export interface CommonField {
  id: string;
  text: string;
  label?: string;
  value?: string | boolean;
  priority?: Priority;
  name: string;
  options?: Array<FormOption>;
  type: FieldType;
  style?: string;
  mask?: string;
}

export interface FormField extends CommonField {
  validations?: string;
  input_type?: string;
  allowSearch?: boolean;
}

export interface FormOption extends CommonField {
  fields?: Array<FormField>;
  multiOption?: boolean;
  optionText?: string;
  optionValue?: string | Array<FormOption>;
}

export type BuildFormOptions = {
  mode: 'iterable' | 'object';
  withMeta?: boolean;
  onlyGroup?: string;
};

export class Form {
  type: 'form';

  fields: Array<FormField>;

  private static extractValue(element: Form | FormField | FormOption, opts?: BuildFormOptions): any {
    if (element.type === 'field' && !element.options) {
      const elmWithMeta = {
        name: element.name,
        label: element.label || element.text,
        value: element.value,
        mask: element.mask,
        priority: element.priority,
      };
      const result = opts?.withMeta ? elmWithMeta : element.value;

      if (!opts.onlyGroup || (element.priority && opts.onlyGroup === element.priority.group)) {
        return result;
      }
    }

    const formOption: FormOption = element as FormOption;
    const optionValue: Array<string> =
      (formOption.optionValue &&
        (Array.isArray(formOption.optionValue)
          ? formOption.optionValue.map(({ id }) => id)
          : [formOption.optionValue])) ||
      [];

    return {
      ...formOption.options
        ?.filter((option) => optionValue.includes(option.id))
        .reduce(
          (accOptions: any, option) =>
            set(
              accOptions,
              option.name,
              this.extractValue({ ...option, priority: option.priority || formOption.priority }, opts),
            ),
          {},
        ),
      ...formOption.fields?.reduce(
        (accFields: any, field) =>
          set(
            accFields,
            field.name,
            this.extractValue({ ...field, priority: field.priority || formOption.priority }, opts),
          ),
        {},
      ),
    };
  }

  static buildFormData(event: Event, opts?: BuildFormOptions): any | Array<any> {
    const formData = (event.steps.filter(({ type }) => type === 'form') as Array<FormStep>)
      .flatMap(({ form }: FormStep) => this.extractValue(form, opts))
      .concat(
        (event.subEvents || []).flatMap(({ cardFields }: SubEvent) =>
          (cardFields || []).reduce((obj: any, cardField) => {
            obj[cardField.name] = this.extractValue(cardField, opts);

            return obj;
          }, {}),
        ),
      )
      .reduce((obj, values) => ({ ...obj, ...values }), opts.onlyGroup ? {} : event.extraFormData || {});

    if (opts && opts.mode === 'iterable') {
      return Object.values(formData).filter(
        (prop) =>
          Object.keys(prop).length &&
          Object.values(prop).some((elm) => Object.values(elm).length && Object.values(elm)[0] !== false),
      );
    }

    return formData;
  }
}
